import { createContext, useContext, ReactNode } from 'react';
import { useSecurityMonitor } from '@/hooks/useSecurityMonitor';
import { useSecurityAudit } from '@/hooks/useSecurityAudit';
import { useAccessControl } from '@/hooks/useAccessControl';

interface SecurityContextValue {
  // Security Monitoring
  logSecurityEvent: ReturnType<typeof useSecurityMonitor>['logSecurityEvent'];
  checkDeviceFingerprint: ReturnType<typeof useSecurityMonitor>['checkDeviceFingerprint'];
  
  // Security Audit
  auditLogs: ReturnType<typeof useSecurityAudit>['auditLogs'];
  logSpreadsheetAction: ReturnType<typeof useSecurityAudit>['logSpreadsheetAction'];
  logAdminAction: ReturnType<typeof useSecurityAudit>['logAdminAction'];
  logAuthAction: ReturnType<typeof useSecurityAudit>['logAuthAction'];
  logDataAccess: ReturnType<typeof useSecurityAudit>['logDataAccess'];
  exportAuditLogs: ReturnType<typeof useSecurityAudit>['exportAuditLogs'];
  
  // Access Control
  canAccess: ReturnType<typeof useAccessControl>['canAccess'];
  checkAccess: ReturnType<typeof useAccessControl>['checkAccess'];
  isAdmin: boolean;
  userPlan: string;
}

const SecurityContext = createContext<SecurityContextValue | null>(null);

interface SecurityProviderProps {
  children: ReactNode;
}

export function SecurityProvider({ children }: SecurityProviderProps) {
  // Initialize all security hooks
  const securityMonitor = useSecurityMonitor();
  const securityAudit = useSecurityAudit({
    enableAutoLogging: true
  });
  const accessControl = useAccessControl();
  
  const value: SecurityContextValue = {
    // Security Monitoring
    logSecurityEvent: securityMonitor.logSecurityEvent,
    checkDeviceFingerprint: securityMonitor.checkDeviceFingerprint,
    
    // Security Audit
    auditLogs: securityAudit.auditLogs,
    logSpreadsheetAction: securityAudit.logSpreadsheetAction,
    logAdminAction: securityAudit.logAdminAction,
    logAuthAction: securityAudit.logAuthAction,
    logDataAccess: securityAudit.logDataAccess,
    exportAuditLogs: securityAudit.exportAuditLogs,
    
    // Access Control
    canAccess: accessControl.canAccess,
    checkAccess: accessControl.checkAccess,
    isAdmin: accessControl.isAdmin,
    userPlan: accessControl.userPlan,
  };
  
  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
}

export function useSecurityContext() {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('useSecurityContext must be used within SecurityProvider');
  }
  return context;
}